import {
  Box, Button, Card, CardContent, Chip, LinearProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, alpha,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HubIcon from '@mui/icons-material/Hub';
import BoltIcon from '@mui/icons-material/Bolt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { api } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/ToastProvider';
import { errMessage } from '../../utils/errMessage';
import { StatWidget } from '../../components/StatWidget';
import { PageHeader } from '../../components/PageHeader';
import { MarketTicker } from '../../components/market/MarketTicker';
import { ConfirmDialog } from '../../components/ConfirmDialog';

const GREEN = '#16c784';
const RED = '#ea3943';
const PIE_COLORS = ['#4f8cff', '#16c784', '#9b8cff', '#f5a623', '#8b97b0'];

interface Exposure { provider: string; equityUsd: number; openPositions: number; openUpnlUsd: number; topAsset: string | null; status: string; }
interface Position { id: number; provider: string; accountLabel: string | null; symbol: string; market: string; side: string; size: number; entryPrice: number | null; markPrice: number | null; liquidationPrice: number | null; marginUsd: number | null; notionalUsd: number | null; realizedPnlUsd: number | null; upnlUsd: number; leverage: number | null; takeProfit: number | null; stopLoss: number | null; }
interface Allocation { asset: string; valueUsd: number; pct: number; }
interface BalanceRow { provider: string; accountLabel: string | null; asset: string; free: number; locked: number; total: number; freeUsd: number; lockedUsd: number; usdValue: number; pct: number; }
interface AccountMarginRow { provider: string; accountLabel: string | null; equityUsd: number; marginUsedUsd: number; availableMarginUsd: number; marginUtilizationPct: number; openPositions: number; openUpnlUsd: number; }
interface Portfolio {
  equityUsd: number; openUpnlUsd: number; openUpnlPct: number;
  venuesConnected: number; venuesTotal: number; activeOrders: number;
  exposure: Exposure[]; positions: Position[]; allocation: Allocation[];
  balances: BalanceRow[];
  accountMargin: AccountMarginRow[];
  wallet: { totalUsd: number; freeUsd: number; lockedUsd: number; assets: number };
  dailyPnl: { hour: number; pnl: number }[];
  risk: {
    totalValueUsd: number; openUpnlUsd: number; marginUsedUsd: number;
    availableMarginUsd: number; freeBalanceUsd: number; lockedBalanceUsd: number;
    notionalUsd: number; marginUtilizationPct: number; leverage: number; openPositions: number;
  };
}
interface Order { id: number; symbol: string; side: string; order_type: string; status: string; quantity: string; created_at: string; }
interface Summary { accounts: number; exchanges: number; pendingOrders: number; openOrders: number; filledOrders: number; totalOrders: number; }
interface Account { id: number; provider: string; label: string | null; status: string; access_mode: string | null; }

const acctStatusColor: Record<string, string> = { CONNECTED: '#16c784', DISCONNECTED: '#f5a623', DISABLED: '#8b97b0', INVALID: '#ea3943' };

const usd = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedUsd = (n: number) => `${n >= 0 ? '+' : '-'}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pnlColor = (n: number) => (n >= 0 ? GREEN : RED);
// Price formatter: adapts precision to magnitude so both BTC ($64,210.50) and low-priced assets ($0.00004210) read cleanly.
const price = (n: number | null) => {
  if (n == null) return '—';
  const dp = Math.abs(n) >= 1 ? 2 : Math.abs(n) >= 0.01 ? 4 : 8;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
};
// Native-unit amount (coin quantity): up to 8 dp, trailing zeros trimmed so
// 0.00450000 BTC reads as 0.0045 and 1200 USDT stays 1,200.
const amt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 8 });

function SectionCard({ title, action, children, sx }: { title: string; action?: React.ReactNode; children: React.ReactNode; sx?: object }) {
  return (
    <Card sx={sx}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6">{title}</Typography>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

export default function ClientDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // View-only clients see the dashboard but can't act on positions (no Close / TP-SL).
  const { isViewOnly } = useAuth();
  const toast = useToast();
  // Position awaiting close confirmation (null = dialog closed).
  const [pendingClose, setPendingClose] = useState<Position | null>(null);
  // How much of the pending position to close (string so the field edits freely).
  // Defaults to the full size; the client can lower it for a partial close.
  const [closeQty, setCloseQty] = useState('');
  // Resting limit price for the close (string; validated in the dialog). All
  // closes are LIMIT orders — the platform only accepts limit orders.
  const [closePrice, setClosePrice] = useState('');
  // Position awaiting a TP/SL modification (null = dialog closed), plus the new
  // take-profit / stop-loss inputs (strings so the fields edit freely).
  const [pendingTpSl, setPendingTpSl] = useState<Position | null>(null);
  const [tpInput, setTpInput] = useState('');
  const [slInput, setSlInput] = useState('');
  const { data: p } = useQuery({
    queryKey: ['portfolio'],
    queryFn: async () => (await api.get('/trading/portfolio')).data.data as Portfolio,
    // Steady 3s cadence matches the backend fast position-sync loop.
    refetchInterval: 3_000,
    refetchOnWindowFocus: true, // returning from the Trade page reflects new opens immediately
  });
  const closePosition = useMutation({
    // Closes are reduce-only LIMIT orders (platform policy): `price` is the resting
    // limit price. `quantity` omitted => full close; provided => partial close.
    // A limit close rests on the exchange until filled, so we never optimistically
    // hide the row — it clears on the next sync once the fill settles.
    mutationFn: ({ id, quantity, price }: { id: number; quantity?: number; price: number }) =>
      api.post(`/trading/positions/${id}/close`, {
        ...(quantity != null ? { quantity } : {}),
        price,
      }),
    onSuccess: () => {
      toast.success('Close request submitted — pending admin approval', 'Close submitted');
      qc.invalidateQueries({ queryKey: ['portfolio'] }); qc.invalidateQueries({ queryKey: ['recent-orders'] });
    },
    onError: (e) => toast.error(errMessage(e, 'Failed to submit close'), 'Close failed'),
  });
  const modifyTpSl = useMutation({
    // Requests a TP/SL change on a position — routed through admin approval like a
    // trade. At least one of takeProfit / stopLoss is sent.
    mutationFn: ({ id, takeProfit, stopLoss }: { id: number; takeProfit?: number; stopLoss?: number }) =>
      api.post(`/trading/positions/${id}/tpsl`, {
        ...(takeProfit != null ? { takeProfit } : {}),
        ...(stopLoss != null ? { stopLoss } : {}),
      }),
    // Close the dialog only on success; on failure keep it open so the validation
    // message (e.g. "take-profit must be above the current price") is shown.
    onSuccess: () => {
      toast.success('TP/SL update submitted — pending admin approval', 'TP/SL submitted');
      setPendingTpSl(null); qc.invalidateQueries({ queryKey: ['portfolio'] }); qc.invalidateQueries({ queryKey: ['recent-orders'] });
    },
  });
  // Human-readable error from the TP/SL request (backend business message, else generic).
  const tpSlError = modifyTpSl.isError
    ? ((modifyTpSl.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Failed to submit TP/SL — please try again')
    : null;
  const { data: orders } = useQuery({
    queryKey: ['recent-orders'],
    queryFn: async () => (await api.get('/trading/orders?limit=6')).data.data.items as Order[],
    refetchInterval: 15_000,
  });
  const { data: summary } = useQuery({
    queryKey: ['client-summary'],
    queryFn: async () => (await api.get('/trading/summary')).data.data as Summary,
    refetchInterval: 15_000,
  });
  const { data: accounts } = useQuery({
    queryKey: ['client-accounts'],
    queryFn: async () => (await api.get('/trading/accounts')).data.data.items as Account[],
  });
  const accts = accounts ?? [];

  const exposure = p?.exposure ?? [];
  const positions = p?.positions ?? [];
  const allocation = p?.allocation ?? [];
  const balances = p?.balances ?? [];
  const accountMargin = p?.accountMargin ?? [];
  const recent = (orders ?? []).slice(0, 6);
  const dailyPnl = (p?.dailyPnl ?? []).map((d) => ({ label: `${String(d.hour).padStart(2, '0')}:00`, pnl: d.pnl }));

  return (
    <Box>
      <PageHeader title="Dashboard" subtitle="Realtime trading, execution, exposure & risk controls" />

      <Box sx={{ mb: 3 }}>
        {/* View-only clients have no trade ticket, so the ticker is non-navigational for them. */}
        <MarketTicker onSelect={(s) => { if (!isViewOnly) navigate(`/trade?symbol=${s}`); }} />
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatWidget label="Portfolio Equity" value={usd(p?.equityUsd ?? 0)} icon={<AccountBalanceWalletIcon />} color="#4f8cff"
          hint={`Across ${p?.venuesTotal ?? 0} exchange${(p?.venuesTotal ?? 0) === 1 ? '' : 's'}`} />
        <StatWidget label="Open uPnL" value={signedUsd(p?.openUpnlUsd ?? 0)} icon={<TrendingUpIcon />} color={pnlColor(p?.openUpnlUsd ?? 0)}
          hint={`${(p?.openUpnlPct ?? 0).toFixed(2)}% of portfolio`} />
        <StatWidget label="Connected Venues" value={`${p?.venuesConnected ?? 0}/${p?.venuesTotal ?? 0}`} icon={<HubIcon />} color="#9b8cff"
          hint={p && p.venuesTotal && p.venuesConnected === p.venuesTotal ? 'All venues connected' : 'Some venues offline'} />
        <StatWidget label="Active Orders" value={p?.activeOrders ?? 0} icon={<BoltIcon />} color="#f5a623" hint="Open & pending" />
      </Box>

      {/* Account / config summary (existing) */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <StatWidget label="Exchanges" value={summary?.exchanges ?? '—'} icon={<AccountBalanceIcon />} color="#4f8cff" />
        <StatWidget label="API Accounts" value={summary?.accounts ?? '—'} icon={<VpnKeyIcon />} color="#9b8cff" />
        <StatWidget label="Open Orders" value={summary?.openOrders ?? '—'} icon={<PlaylistAddCheckIcon />} color="#16c784" />
        <StatWidget label="Pending Approval" value={summary?.pendingOrders ?? '—'} icon={<PendingActionsIcon />} color="#f5a623" />
        <StatWidget label="Filled Orders" value={summary?.filledOrders ?? '—'} icon={<TaskAltIcon />} color="#16c784" />
      </Box>

      {/* Quick actions — trade shortcuts. Hidden entirely for view-only clients. */}
      {!isViewOnly && (
        <SectionCard
          title="Quick Actions"
          action={<Chip size="small" label="Live" sx={{ color: GREEN, backgroundColor: alpha(GREEN, 0.14), border: `1px solid ${alpha(GREEN, 0.35)}` }} />}
          sx={{ mb: 3 }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {['BINANCE', 'BYBIT', 'DERIBIT'].map((ex) => (
              <Button key={ex} variant="outlined" onClick={() => navigate('/trade')} sx={{ flex: '1 1 160px' }}>
                Trade on {ex.charAt(0) + ex.slice(1).toLowerCase()}
              </Button>
            ))}
            <Button variant="contained" color="success" onClick={() => navigate('/orders')} sx={{ flex: '1 1 160px' }}>
              View Orders
            </Button>
          </Box>
        </SectionCard>
      )}

      {/* Exchange exposure */}
      <SectionCard
        title="Exchange Exposure"
        action={<Chip size="small" label="Live Overview" sx={{ color: '#f5a623', backgroundColor: alpha('#f5a623', 0.14), border: `1px solid ${alpha('#f5a623', 0.35)}` }} />}
        sx={{ mb: 3, p: 0 }}
      >
        <TableContainer>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Exchange</TableCell>
                <TableCell align="right">Equity (USDT)</TableCell>
                <TableCell align="right">Open Positions</TableCell>
                <TableCell align="right">Open uPnL</TableCell>
                <TableCell>Top Asset</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exposure.map((e) => (
                <TableRow key={e.provider} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{e.provider}</TableCell>
                  <TableCell align="right" className="mono">{usd(e.equityUsd)}</TableCell>
                  <TableCell align="right" className="mono">{e.openPositions}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: pnlColor(e.openUpnlUsd) }}>{signedUsd(e.openUpnlUsd)}</TableCell>
                  <TableCell>{e.topAsset ?? '—'}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={e.status}
                      sx={{ color: e.status === 'CONNECTED' ? GREEN : '#8b97b0', backgroundColor: alpha(e.status === 'CONNECTED' ? GREEN : '#8b97b0', 0.14), border: `1px solid ${alpha(e.status === 'CONNECTED' ? GREEN : '#8b97b0', 0.35)}` }} />
                  </TableCell>
                </TableRow>
              ))}
              {!exposure.length && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No exchange accounts connected yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* Wallet Balances — full per-asset breakdown: free vs locked in native units and USD */}
      <SectionCard
        title="Wallet Balances"
        action={
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total <Box component="span" className="mono" sx={{ color: 'text.primary', fontWeight: 700 }}>{usd(p?.wallet?.totalUsd ?? 0)}</Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Free <Box component="span" className="mono" sx={{ color: GREEN, fontWeight: 700 }}>{usd(p?.wallet?.freeUsd ?? 0)}</Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Locked <Box component="span" className="mono" sx={{ color: '#f5a623', fontWeight: 700 }}>{usd(p?.wallet?.lockedUsd ?? 0)}</Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Margin Used <Box component="span" className="mono" sx={{ color: '#9b8cff', fontWeight: 700 }}>{usd(p?.risk?.marginUsedUsd ?? 0)}</Box>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Avail. Margin <Box component="span" className="mono" sx={{ color: '#4f8cff', fontWeight: 700 }}>{usd(p?.risk?.availableMarginUsd ?? 0)}</Box>
            </Typography>
          </Box>
        }
        sx={{ mb: 3, p: 0 }}
      >
        <TableContainer>
          <Table size="small" sx={{ minWidth: 940 }}>
            <TableHead>
              <TableRow>
                <TableCell>Exchange</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell align="right">Free</TableCell>
                <TableCell align="right">Locked</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Free (USD)</TableCell>
                <TableCell align="right">Locked (USD)</TableCell>
                <TableCell align="right">Value (USD)</TableCell>
                <TableCell align="right">Allocation</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {balances.map((b) => (
                <TableRow key={`${b.provider}-${b.accountLabel ?? ''}-${b.asset}`} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{b.provider}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{b.accountLabel || '—'}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{b.asset}</TableCell>
                  <TableCell align="right" className="mono">{amt(b.free)}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: b.locked > 0 ? '#f5a623' : 'inherit' }}>{amt(b.locked)}</TableCell>
                  <TableCell align="right" className="mono">{amt(b.total)}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: 'text.secondary' }}>{usd(b.freeUsd)}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: 'text.secondary' }}>{usd(b.lockedUsd)}</TableCell>
                  <TableCell align="right" className="mono" sx={{ fontWeight: 700 }}>{usd(b.usdValue)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <Box sx={{ width: 54 }}>
                        <LinearProgress variant="determinate" value={Math.min(100, b.pct)} sx={{ height: 6, borderRadius: 3 }} />
                      </Box>
                      <Typography variant="caption" className="mono" sx={{ minWidth: 42, textAlign: 'right' }}>{b.pct.toFixed(1)}%</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!balances.length && (
                <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No wallet balances synced yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* Margin by Account — derivatives collateral view, per exchange account */}
      <SectionCard
        title="Margin by Account"
        action={<Chip size="small" label="Derivatives" sx={{ color: '#9b8cff', backgroundColor: alpha('#9b8cff', 0.14), border: `1px solid ${alpha('#9b8cff', 0.35)}` }} />}
        sx={{ mb: 3, p: 0 }}
      >
        <TableContainer>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>Exchange</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Equity (USD)</TableCell>
                <TableCell align="right">Margin Used</TableCell>
                <TableCell align="right">Available Margin</TableCell>
                <TableCell align="right">Open Positions</TableCell>
                <TableCell align="right">Open uPnL</TableCell>
                <TableCell align="right">Margin Utilization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accountMargin.map((m) => (
                <TableRow key={`${m.provider}-${m.accountLabel ?? ''}`} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{m.provider}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{m.accountLabel || '—'}</TableCell>
                  <TableCell align="right" className="mono">{usd(m.equityUsd)}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: m.marginUsedUsd > 0 ? '#9b8cff' : 'inherit' }}>{usd(m.marginUsedUsd)}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: '#4f8cff' }}>{usd(m.availableMarginUsd)}</TableCell>
                  <TableCell align="right" className="mono">{m.openPositions}</TableCell>
                  <TableCell align="right" className="mono" sx={{ color: pnlColor(m.openUpnlUsd) }}>{signedUsd(m.openUpnlUsd)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <Box sx={{ width: 60 }}>
                        <LinearProgress variant="determinate" value={Math.min(100, m.marginUtilizationPct)} sx={{ height: 6, borderRadius: 3 }} />
                      </Box>
                      <Typography variant="caption" className="mono" sx={{ minWidth: 42, textAlign: 'right' }}>{m.marginUtilizationPct.toFixed(1)}%</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {!accountMargin.length && (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No accounts connected yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* Active Positions — full width so the Close action is always visible (no horizontal scroll) */}
      <SectionCard title="Active Positions" sx={{ mb: 3 }}>
        <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Exchange</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Pair</TableCell><TableCell>Side</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Avg Price</TableCell>
                  <TableCell align="right">Mark Price</TableCell>
                  <TableCell align="right">Liq. Price</TableCell>
                  <TableCell align="right">Notional</TableCell>
                  <TableCell align="right">Margin</TableCell>
                  <TableCell align="right">Leverage</TableCell>
                  <TableCell align="right">uPnL</TableCell>
                  <TableCell align="right">Realized PnL</TableCell>
                  <TableCell align="right">TP / SL</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((pos) => (
                  <TableRow key={pos.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{pos.provider || '—'}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{pos.accountLabel || '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {pos.symbol}
                      <Box component="span" sx={{ ml: 0.75, px: 0.6, py: '1px', borderRadius: '4px', fontSize: 10, fontWeight: 700, color: '#9b8cff', backgroundColor: alpha('#9b8cff', 0.14), border: `1px solid ${alpha('#9b8cff', 0.3)}` }}>
                        {pos.market === 'FUTURES' ? 'PERP' : pos.market}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: pos.side === 'SHORT' ? RED : GREEN, fontWeight: 700 }}>{pos.side}</TableCell>
                    <TableCell align="right" className="mono">{pos.size}</TableCell>
                    <TableCell align="right" className="mono">{price(pos.entryPrice)}</TableCell>
                    <TableCell align="right" className="mono">{price(pos.markPrice)}</TableCell>
                    <TableCell align="right" className="mono" sx={{ color: pos.liquidationPrice != null ? RED : 'inherit' }}>{price(pos.liquidationPrice)}</TableCell>
                    <TableCell align="right" className="mono">{pos.notionalUsd != null ? usd(pos.notionalUsd) : '—'}</TableCell>
                    <TableCell align="right" className="mono">{pos.marginUsd != null ? usd(pos.marginUsd) : '—'}</TableCell>
                    <TableCell align="right" className="mono">{pos.leverage != null ? `${pos.leverage}x` : '—'}</TableCell>
                    <TableCell align="right" className="mono" sx={{ color: pnlColor(pos.upnlUsd) }}>{signedUsd(pos.upnlUsd)}</TableCell>
                    <TableCell align="right" className="mono" sx={{ color: pos.realizedPnlUsd != null ? pnlColor(pos.realizedPnlUsd) : 'inherit' }}>{pos.realizedPnlUsd != null ? signedUsd(pos.realizedPnlUsd) : '—'}</TableCell>
                    <TableCell align="right" className="mono" sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                      <Box component="span" sx={{ color: pos.takeProfit != null ? GREEN : 'text.secondary' }}>{pos.takeProfit != null ? price(pos.takeProfit) : '—'}</Box>
                      <Box component="span" sx={{ color: 'text.secondary', mx: 0.5 }}>/</Box>
                      <Box component="span" sx={{ color: pos.stopLoss != null ? RED : 'text.secondary' }}>{pos.stopLoss != null ? price(pos.stopLoss) : '—'}</Box>
                    </TableCell>
                    <TableCell align="right">
                      {isViewOnly ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>View only</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {/* Deribit doesn't support attached TP/SL yet — hide the action there. */}
                          {(pos.provider || '').toUpperCase() !== 'DERIBIT' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                modifyTpSl.reset(); // clear any prior error
                                setPendingTpSl(pos);
                                // Prefill with the position's current TP/SL so the client edits from
                                // the live values rather than from blank.
                                setTpInput(pos.takeProfit != null ? String(pos.takeProfit) : '');
                                setSlInput(pos.stopLoss != null ? String(pos.stopLoss) : '');
                              }}
                            >
                              TP/SL
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              setPendingClose(pos);
                              setCloseQty(String(pos.size));
                              // Prefill the limit price with the current mark as a starting point.
                              setClosePrice(pos.markPrice != null ? String(pos.markPrice) : '');
                            }}
                          >
                            Close
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!positions.length && <TableRow><TableCell colSpan={15} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No open positions.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
      </SectionCard>

      {/* Recent orders | Risk */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'stretch', mb: 3 }}>
        <SectionCard title="Recent Orders" action={<Button size="small" onClick={() => navigate('/orders')}>View All</Button>} sx={{ flex: '1 1 340px', minWidth: 300 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pair</TableCell><TableCell>Side</TableCell><TableCell>Type</TableCell><TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{o.symbol}</TableCell>
                    <TableCell sx={{ color: o.side === 'SELL' ? RED : GREEN, fontWeight: 700 }}>{o.side}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{o.order_type}</TableCell>
                    <TableCell align="right"><Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{o.status}</Typography></TableCell>
                  </TableRow>
                ))}
                {!recent.length && <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No orders yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>

        <SectionCard title="Risk Overview" sx={{ flex: '1 1 300px', minWidth: 280 }}>
          {[
            ['Total Portfolio Value', usd(p?.risk?.totalValueUsd ?? 0)],
            ['Total Open uPnL', signedUsd(p?.risk?.openUpnlUsd ?? 0)],
            ['Free Balance', usd(p?.risk?.freeBalanceUsd ?? 0)],
            ['Locked / In Orders', usd(p?.risk?.lockedBalanceUsd ?? 0)],
            ['Total Margin Used', usd(p?.risk?.marginUsedUsd ?? 0)],
            ['Available Margin', usd(p?.risk?.availableMarginUsd ?? 0)],
            ['Total Notional Exposure', usd(p?.risk?.notionalUsd ?? 0)],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{k}</Typography>
              <Typography className="mono" sx={{ fontWeight: 600, color: k === 'Total Open uPnL' ? pnlColor(p?.risk?.openUpnlUsd ?? 0) : undefined }}>{v}</Typography>
            </Box>
          ))}
          <Box sx={{ py: 0.75 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Margin Utilization</Typography>
              <Typography className="mono" sx={{ fontWeight: 600 }}>{(p?.risk?.marginUtilizationPct ?? 0).toFixed(1)}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={Math.min(100, p?.risk?.marginUtilizationPct ?? 0)} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Account Leverage</Typography>
            <Typography className="mono" sx={{ fontWeight: 600 }}>{(p?.risk?.leverage ?? 0).toFixed(2)}x</Typography>
          </Box>
        </SectionCard>
      </Box>

      {/* Allocation | Daily PnL */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <SectionCard title="Portfolio Allocation" sx={{ flex: '1 1 320px', minWidth: 300 }}>
          {allocation.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={allocation} dataKey="valueUsd" nameKey="asset" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none">
                      {allocation.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#141d33', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10 }}
                      itemStyle={{ color: '#e6edf7' }}
                      labelStyle={{ color: '#e6edf7' }}
                      formatter={(v, name) => [usd(v as number), name as string]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ flex: 1, minWidth: 140 }}>
                {allocation.map((a, i) => (
                  <Box key={a.asset} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>{a.asset}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.pct.toFixed(1)}%</Typography>
                    <Typography variant="caption" className="mono" sx={{ color: 'text.secondary', minWidth: 80, textAlign: 'right' }}>{usd(a.valueUsd)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No balances to allocate yet.</Typography>
          )}
        </SectionCard>

        <SectionCard
          title="Daily P&L (USDT)"
          action={<Typography variant="caption" sx={{ color: pnlColor(p?.openUpnlUsd ?? 0), fontWeight: 700 }}>Today: {signedUsd(dailyPnl.reduce((s, d) => s + d.pnl, 0))}</Typography>}
          sx={{ flex: '1 1 420px', minWidth: 300 }}
        >
          <Box sx={{ height: 220 }}>
            {dailyPnl.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyPnl} margin={{ left: -12, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: '#7c89a3', fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                  <YAxis tick={{ fill: '#7c89a3', fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={{ background: '#141d33', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10 }} itemStyle={{ color: '#e6edf7' }} labelStyle={{ color: '#e6edf7' }} formatter={(v) => [signedUsd(v as number), 'P&L']} />
                  <Bar dataKey="pnl" radius={[3, 3, 0, 0]}>
                    {dailyPnl.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? GREEN : RED} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>No realized P&L today.</Typography>
              </Box>
            )}
          </Box>
        </SectionCard>
      </Box>

      {/* Your accounts & permissions (existing) */}
      <SectionCard title="Your Accounts & Permissions" sx={{ mt: 3, p: 0 }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Exchange</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Permission</TableCell>
                <TableCell align="right">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accts.map((a) => {
                const sc = acctStatusColor[a.status] ?? '#8b97b0';
                return (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{a.provider}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{a.label || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label="Approval Required"
                        sx={{ color: '#f5a623', backgroundColor: alpha('#f5a623', 0.14), border: `1px solid ${alpha('#f5a623', 0.35)}` }} />
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={a.status}
                        sx={{ color: sc, backgroundColor: alpha(sc, 0.14), border: `1px solid ${alpha(sc, 0.35)}` }} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {!accts.length && (
                <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No exchange accounts yet. Your admin will provision them.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      {/* TP/SL modification dialog — submits a take-profit / stop-loss change for
          admin approval (same flow as a trade). At least one leg is required. */}
      {(() => {
        const numRe = /^\d+(\.\d+)?$/;
        const tpStr = tpInput.trim();
        const slStr = slInput.trim();
        const tpNum = Number(tpStr);
        const slNum = Number(slStr);
        const tpValid = tpStr === '' || (numRe.test(tpStr) && tpNum > 0);
        const slValid = slStr === '' || (numRe.test(slStr) && slNum > 0);
        const hasTp = tpStr !== '' && tpValid;
        const hasSl = slStr !== '' && slValid;
        // TP/SL must be on the correct side of the current price or the exchange
        // rejects them (they'd trigger immediately). Long: TP above, SL below the
        // mark; short: the reverse. Validated here for instant feedback.
        const mark = pendingTpSl?.markPrice ?? 0;
        const isLong = pendingTpSl?.side === 'SHORT' ? false : pendingTpSl?.side === 'LONG' ? true : (pendingTpSl?.size ?? 0) >= 0;
        const tpDirOk = !hasTp || mark <= 0 || (isLong ? tpNum > mark : tpNum < mark);
        const slDirOk = !hasSl || mark <= 0 || (isLong ? slNum < mark : slNum > mark);
        const canConfirm = !!pendingTpSl && tpValid && slValid && tpDirOk && slDirOk && (hasTp || hasSl);
        const tpHelp = !tpValid ? 'Enter a price greater than 0'
          : !tpDirOk ? `Must be ${isLong ? 'above' : 'below'} the current price`
          : 'Blank = no take-profit';
        const slHelp = !slValid ? 'Enter a price greater than 0'
          : !slDirOk ? `Must be ${isLong ? 'below' : 'above'} the current price`
          : 'Blank = no stop-loss';
        return (
          <ConfirmDialog
            open={!!pendingTpSl}
            title="Update take-profit / stop-loss?"
            confirmLabel="Submit TP/SL"
            confirmColor="primary"
            severity="warning"
            loading={modifyTpSl.isPending}
            confirmDisabled={!canConfirm}
            onCancel={() => { modifyTpSl.reset(); setPendingTpSl(null); }}
            onConfirm={() => {
              // Close on success (mutation onSuccess); stay open on error so the
              // message shows. Don't close here.
              if (pendingTpSl && canConfirm) {
                modifyTpSl.mutate({
                  id: pendingTpSl.id,
                  takeProfit: hasTp ? tpNum : undefined,
                  stopLoss: hasSl ? slNum : undefined,
                });
              }
            }}
            warning="This submits a take-profit / stop-loss change for admin approval. Once approved it is applied to the live position on the exchange, replacing any existing TP/SL triggers."
          >
            {pendingTpSl && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.75, columnGap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Pair</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
                  {pendingTpSl.symbol} ({pendingTpSl.market === 'FUTURES' ? 'PERP' : pendingTpSl.market})
                </Typography>
                <Typography variant="body2" color="text.secondary">Side</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: pendingTpSl.side === 'SHORT' ? RED : GREEN }}>
                  {pendingTpSl.side}
                </Typography>
                <Typography variant="body2" color="text.secondary">Mark Price</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{price(pendingTpSl.markPrice)}</Typography>

                <Typography variant="body2" color="text.secondary">Take Profit</Typography>
                <TextField
                  value={tpInput}
                  onChange={(e) => setTpInput(e.target.value)}
                  size="small"
                  error={!tpValid || !tpDirOk}
                  placeholder="0.00"
                  helperText={tpHelp}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />

                <Typography variant="body2" color="text.secondary">Stop Loss</Typography>
                <TextField
                  value={slInput}
                  onChange={(e) => setSlInput(e.target.value)}
                  size="small"
                  error={!slValid || !slDirOk}
                  placeholder="0.00"
                  helperText={slHelp}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />

                {tpSlError && (
                  <Box sx={{ gridColumn: '1 / -1', mt: 0.5, color: RED, fontSize: 13 }}>{tpSlError}</Box>
                )}
              </Box>
            )}
          </ConfirmDialog>
        );
      })()}

      {(() => {
        // Plain-text fields with manual numeric validation (no native number input).
        // A positive-decimal pattern keeps stray characters / signs / exponents out.
        const numRe = /^\d+(\.\d+)?$/;
        const fullSize = pendingClose ? Math.abs(Number(pendingClose.size)) : 0;

        // Amount to close: blank => close the full position; otherwise must parse
        // and be > 0 and ≤ the open size. Below the full size = partial close.
        const qtyStr = closeQty.trim();
        const qtyNum = Number(qtyStr);
        const qtyValid = qtyStr === '' || (numRe.test(qtyStr) && qtyNum > 0 && qtyNum <= fullSize);
        const isPartial = qtyStr !== '' && qtyValid && qtyNum < fullSize;

        // Closes are LIMIT-only — a positive resting price is always required.
        const priceStr = closePrice.trim();
        const priceNum = Number(priceStr);
        const priceValid = numRe.test(priceStr) && priceNum > 0;

        const canConfirm = !!pendingClose && qtyValid && priceValid;
        const label = `${isPartial ? 'Close Partially' : 'Close Position'} (Limit)`;
        return (
          <ConfirmDialog
            open={!!pendingClose}
            title={isPartial ? 'Close part of position?' : 'Close position?'}
            confirmLabel={label}
            confirmColor="error"
            severity="warning"
            confirmDisabled={!canConfirm}
            onCancel={() => setPendingClose(null)}
            onConfirm={() => {
              if (pendingClose && canConfirm) {
                // Reduce-only LIMIT close: send the resting price, plus a quantity
                // only for a genuine partial close.
                closePosition.mutate({
                  id: pendingClose.id,
                  quantity: isPartial ? qtyNum : undefined,
                  price: priceNum,
                });
              }
              setPendingClose(null);
            }}
            warning={`This submits a reduce-only LIMIT order to close ${isPartial ? 'part of' : ''} the position at your price. It rests on the live exchange until filled (it may not fill). This cannot be undone once executed.`}
          >
            {pendingClose && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.75, columnGap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Pair</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
                  {pendingClose.symbol} ({pendingClose.market === 'FUTURES' ? 'PERP' : pendingClose.market})
                </Typography>
                <Typography variant="body2" color="text.secondary">Side</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: pendingClose.side === 'SHORT' ? RED : GREEN }}>
                  {pendingClose.side}
                </Typography>
                <Typography variant="body2" color="text.secondary">Open Size</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{pendingClose.size}</Typography>
                <Typography variant="body2" color="text.secondary">Entry / Mark</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">
                  {price(pendingClose.entryPrice)} / {price(pendingClose.markPrice)}
                </Typography>
                <Typography variant="body2" color="text.secondary">Unrealized PnL</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: pnlColor(pendingClose.upnlUsd) }} className="mono">
                  {signedUsd(pendingClose.upnlUsd)}
                </Typography>

                {/* Closes are LIMIT-only — the resting price is always required. */}
                <Typography variant="body2" color="text.secondary">Order type</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>Limit</Typography>

                <Typography variant="body2" color="text.secondary">Closing price</Typography>
                <TextField
                  value={closePrice}
                  onChange={(e) => setClosePrice(e.target.value)}
                  size="small"
                  error={!priceValid}
                  placeholder="0.00"
                  helperText={!priceValid ? 'Enter a price greater than 0' : 'Order rests until filled at this price'}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />

                <Typography variant="body2" color="text.secondary">Amount to close</Typography>
                <TextField
                  value={closeQty}
                  onChange={(e) => setCloseQty(e.target.value)}
                  size="small"
                  error={!qtyValid}
                  helperText={!qtyValid ? `Enter an amount between 0 and ${fullSize}` : 'Lower this to close only part of the position'}
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
                />
                <Box />
                <Button size="small" sx={{ justifySelf: 'end', mt: -0.5 }} onClick={() => setCloseQty(String(fullSize))}>
                  Max
                </Button>
              </Box>
            )}
          </ConfirmDialog>
        );
      })()}
    </Box>
  );
}
